import {
  CreateUserRequest,
  PaginationMetadata,
  UpdateUserRequest,
  UserCardDTO,
  UserDTO,
} from '@/models';
import { rfidAuthService } from '@/services/api';
import { useInstitutionStore, useUIStore, useUserStore } from '@/store';
import { getErrorMessage } from '@/utils/getErrorMessage';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;
const DEFAULT_PAGE_SIZE = 10;

/**
 * Manages user CRUD operations, pagination, and modal state
 * for the User Management page (v2 API)
 */
export const useUserManagement = () => {
  const {
    users,
    isLoading,
    error,
    lastCreatedCredentials,
    fetchUsers,
    createUser,
    updateUser,
    deleteUser,
    regenerateTempPassword,
    clearError,
    clearCredentials,
  } = useUserStore();

  const { institutions, fetchInstitutions } = useInstitutionStore();
  const { addNotification } = useUIStore();

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [selectedUser, setSelectedUser] = useState<UserDTO | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState<UserDTO | null>(null);
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [userToChangePassword, setUserToChangePassword] = useState<UserDTO | null>(null);
  const [formData, setFormData] = useState<CreateUserRequest>({
    institutionId: '',
    email: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
  });
  const [isSaving, setIsSaving] = useState(false);
  const [unlinkingCardId, setUnlinkingCardId] = useState<string | null>(null);
  const [isAssigningCard, setIsAssigningCard] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const filteredUsers = useMemo(() => {
    const normalizedSearchTerm = searchTerm.trim().toLowerCase();
    if (!normalizedSearchTerm) return users;

    return users.filter((user) => {
      const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim().toLowerCase();
      const email = (user.email || '').toLowerCase();
      const institution = (user.institution?.name || '').toLowerCase();
      const rfidTags = (user.cards || [])
        .map((card) => (card.rfidTag || '').toLowerCase())
        .join(' ');

      return [fullName, email, institution, rfidTags].some((value) =>
        value.includes(normalizedSearchTerm)
      );
    });
  }, [users, searchTerm]);

  const paginatedUsers = useMemo(
    () => filteredUsers.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    [filteredUsers, currentPage, pageSize]
  );

  const pagination = useMemo<PaginationMetadata>(() => {
    const totalCount = filteredUsers.length;
    const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
    return {
      currentPage,
      pageSize,
      totalCount,
      totalPages,
      hasNext: currentPage < totalPages,
      hasPrevious: currentPage > 1,
    };
  }, [filteredUsers.length, currentPage, pageSize]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    fetchInstitutions(1, 100);
  }, [fetchInstitutions]);

  const handlePageSizeChange = useCallback((newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(1);
  }, []);

  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  }, []);

  const handleCreateClick = useCallback(() => {
    setModalMode('create');
    setSelectedUser(null);
    setFormData({
      institutionId: '',
      email: '',
      firstName: '',
      lastName: '',
      phoneNumber: '',
    });
    setShowModal(true);
    clearError();
  }, [clearError]);

  const handleEditClick = useCallback(
    (user: UserDTO) => {
      setModalMode('edit');
      setSelectedUser(user);
      setFormData({
        institutionId: user.institutionId,
        email: user.email || '',
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        phoneNumber: user.phoneNumber || '',
      });
      setShowModal(true);
      clearError();
    },
    [clearError]
  );

  const handleDeleteClick = useCallback(
    (user: UserDTO) => {
      setUserToDelete(user);
      setShowDeleteModal(true);
      clearError();
    },
    [clearError]
  );

  const handleChangePasswordClick = useCallback(
    (user: UserDTO) => {
      setUserToChangePassword(user);
      setShowChangePasswordModal(true);
      clearError();
      clearCredentials();
    },
    [clearError, clearCredentials]
  );

  const handleConfirmChangePassword = useCallback(async () => {
    if (!userToChangePassword) return;
    setIsSaving(true);
    try {
      await regenerateTempPassword(userToChangePassword.institutionId, userToChangePassword.id);
    } catch (err: unknown) {
      addNotification({
        type: 'error',
        message: getErrorMessage(err, 'Failed to regenerate password'),
        duration: 3000,
      });
      setShowChangePasswordModal(false);
      setUserToChangePassword(null);
    } finally {
      setIsSaving(false);
    }
  }, [userToChangePassword, regenerateTempPassword, addNotification]);

  const handleSubmit = useCallback(async () => {
    setIsSaving(true);
    try {
      if (modalMode === 'create') {
        await createUser(formData);
        addNotification({ type: 'success', message: 'User created successfully', duration: 2000 });
        setShowModal(false);
        // Show credentials modal after successful creation
        setShowCredentialsModal(true);
      } else if (selectedUser) {
        const updateData: UpdateUserRequest = {
          email: formData.email || null,
          firstName: formData.firstName || null,
          lastName: formData.lastName || null,
          phoneNumber: formData.phoneNumber || null,
        };
        await updateUser(selectedUser.institutionId, selectedUser.id, updateData);
        addNotification({ type: 'success', message: 'User updated successfully', duration: 2000 });
        setShowModal(false);
      }
    } catch (err: unknown) {
      addNotification({
        type: 'error',
        message: getErrorMessage(err, 'Operation failed'),
        duration: 3000,
      });
    } finally {
      setIsSaving(false);
    }
  }, [modalMode, createUser, formData, selectedUser, updateUser, addNotification]);

  const handleDelete = useCallback(async () => {
    if (!userToDelete) return;
    setIsSaving(true);
    try {
      await deleteUser(userToDelete.institutionId, userToDelete.id);
      addNotification({ type: 'success', message: 'User deleted successfully', duration: 2000 });
      setShowDeleteModal(false);
    } catch (err: unknown) {
      addNotification({
        type: 'error',
        message: getErrorMessage(err, 'Operation failed'),
        duration: 3000,
      });
    } finally {
      setIsSaving(false);
    }
  }, [userToDelete, deleteUser, addNotification]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    },
    []
  );

  const handleUnlinkCard = useCallback(
    async (card: UserCardDTO) => {
      if (!selectedUser) return;

      setUnlinkingCardId(card.id);

      try {
        await rfidAuthService.revokeAssignedCard({
          institutionId: selectedUser.institutionId,
          userId: selectedUser.id,
          cardId: card.id,
        });

        setSelectedUser((prev) =>
          prev
            ? {
                ...prev,
                cards: (prev.cards || []).filter((userCard) => userCard.id !== card.id),
              }
            : prev
        );

        await fetchUsers();
        addNotification({
          type: 'success',
          message: 'Card unlinked successfully',
          duration: 2000,
        });
      } catch (err: unknown) {
        addNotification({
          type: 'error',
          message: getErrorMessage(err, 'Failed to unlink card'),
          duration: 3000,
        });
      } finally {
        setUnlinkingCardId(null);
      }
    },
    [selectedUser, fetchUsers, addNotification]
  );

  const handleAssignCard = useCallback(
    async (tag: string, expiresAt?: string) => {
      if (!selectedUser) return;

      setIsAssigningCard(true);

      try {
        await rfidAuthService.assignCard({
          userId: selectedUser.id,
          tag,
          expiresAt: expiresAt ? new Date(expiresAt).toISOString() : undefined,
        });

        await fetchUsers();

        // Update selectedUser cards from refreshed users list
        const refreshedUsers = useUserStore.getState().users;
        const refreshed = refreshedUsers.find((u) => u.id === selectedUser.id);
        if (refreshed) {
          setSelectedUser(refreshed);
        }

        addNotification({
          type: 'success',
          message: 'RFID card assigned successfully',
          duration: 2000,
        });
      } catch (err: unknown) {
        addNotification({
          type: 'error',
          message: getErrorMessage(err, 'Failed to assign RFID card'),
          duration: 3000,
        });
      } finally {
        setIsAssigningCard(false);
      }
    },
    [selectedUser, fetchUsers, addNotification]
  );

  const closeModal = useCallback(() => {
    setShowModal(false);
    setSelectedUser(null);
    setUnlinkingCardId(null);
  }, []);
  const closeDeleteModal = useCallback(() => setShowDeleteModal(false), []);
  const closeCredentialsModal = useCallback(() => {
    setShowCredentialsModal(false);
    clearCredentials();
  }, [clearCredentials]);
  const closeChangePasswordModal = useCallback(() => {
    setShowChangePasswordModal(false);
    setUserToChangePassword(null);
    clearCredentials();
  }, [clearCredentials]);
  return {
    users: paginatedUsers,
    institutions,
    pagination,
    isLoading,
    error,
    searchTerm,
    currentPage,
    pageSize,
    pageSizeOptions: PAGE_SIZE_OPTIONS,
    showModal,
    modalMode,
    selectedUser,
    showDeleteModal,
    userToDelete,
    showCredentialsModal,
    lastCreatedCredentials,
    showChangePasswordModal,
    userToChangePassword,
    formData,
    isSaving,
    unlinkingCardId,
    isAssigningCard,
    setCurrentPage,
    handlePageSizeChange,
    handleSearchChange,
    handleCreateClick,
    handleEditClick,
    handleDeleteClick,
    handleChangePasswordClick,
    handleConfirmChangePassword,
    handleSubmit,
    handleDelete,
    handleInputChange,
    handleUnlinkCard,
    handleAssignCard,
    closeModal,
    closeDeleteModal,
    closeCredentialsModal,
    closeChangePasswordModal,
    clearError,
  };
};
